import React, { useState, useContext } from 'react';
import Select from 'react-select';
import { CountryIconOption } from 'components/Form';
import cx from 'classnames';
import styled from 'styled-components';
import { colorStyle } from 'grommet-styles';
import { edgeStyle } from 'grommet/utils';
import { withNamespaces } from 'react-i18next';
import _ from 'lodash';
import { ResponsiveContext } from 'grommet';

const StyledReactSelect = styled(Select)`
  ${(props) => props.margin && edgeStyle('margin', props.margin, props.responsive, props.theme.box.responsiveBreakpoint, props.theme)}

  .react-select {
    &__control {
      ${(props) => props.background && colorStyle('background', props.background, props.theme)}
      border-width: 1px;
      border-style: solid;
      border-radius: 15px;
      ${(props) => props.borderColor && colorStyle('border-color', props.borderColor, props.theme)};
      ${(props) =>
        props.padding && edgeStyle('padding', props.padding, props.responsive, props.theme.box.responsiveBreakpoint, props.theme)}
    }

    &__indicator-separator {
      display: none;
    }

    &__option {
      cursor: pointer;
      align-items: center;

      &--is-focused {
        background-color: rgba(255, 255, 255, 0.07);
        transition: background-color 0.2s;
      }

      &--is-selected {
        background-color: rgba(255, 255, 255, 0.3);
        transition: background-color 0.2s;
      }

      &:active {
        background-color: rgba(255, 255, 255, 0.4);
      }
    }

    &__menu {
      animation: fadeIn 0.3s ease-in-out;
      ${(props) => props.menuBackground && colorStyle('background', props.menuBackground, props.theme)}
      background-color: #FFFFFF08;
      border-radius: 15px;
      backdrop-filter: blur(150px);
      box-shadow: 0px 26px 28px -6px #00000057;
      border: 1px solid #38444a;
      z-index: 10;
      opacity: 1;
    }

    &__menu-list {
      padding: 0;
      border-radius: 15px;
      z-index: 10;

      &::-webkit-scrollbar {
        width: 4px;
      }
      &::-webkit-scrollbar-thumb {
        background-color: #888;
        border-radius: 10px;
      }
      &::-webkit-scrollbar-track {
        background-color: tranparent;
        border-radius: 10px;
      }
    }

    &__dropdown-indicator svg {
      fill: #f8f8f8;
      stroke: #f8f8f8;
      width: 24px;
      height: 24px;

      @media (max-width: 1023px) {
        width: 17px;
        height: 17px;
      }
    }

    &__single-value {
      font-weight: 500;
      font-size: 14px;

      div {
        align-items: center;

        span:nth-of-type(2) {
          display: none;
        }
      }
    }

    &__placeholder {
      font-size: 14px;
      font-weight: 600;
      color: #b4b6b7;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0.5;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const ReactSelect = withNamespaces()(
  ({
    className,
    background = 'selectBackground',
    menuBackground = 'selectBackground',
    borderColor = 'border',
    margin = { bottom: '30px' },
    padding,
    placeholder,
    t,
    isMulti,
    closeMenuOnSelect,
    ...rest
  }) => {
    const [selected, setSelected] = useState(false);
    const size = useContext(ResponsiveContext);

    const handleMenuOpen = () => {
      setSelected(true);
    };

    const handleMenuClose = () => {
      setSelected(false);
    };

    if (size === 'small') {
      // todo: временное решение, надо обновить react select
      return (
        <StyledReactSelect
          {...rest}
          background={background}
          borderColor={borderColor}
          margin={margin}
          padding={padding}
          menuBackground={menuBackground}
          classNamePrefix={'react-select'}
          className={cx('react-select-container', className)}
          controlShouldRenderValue={_.isEqual(isMulti, 'isMulti') ? true : !selected}
          isSearchable={false}
          placeholder={placeholder || t('forms.common.select')}
          isMulti={isMulti}
          closeMenuOnSelect={closeMenuOnSelect}
        />
      );
    }

    return (
      <StyledReactSelect
        {...rest}
        background={background}
        borderColor={borderColor}
        margin={margin}
        padding={padding}
        menuBackground={menuBackground}
        classNamePrefix={'react-select'}
        className={cx('react-select-container', className)}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        controlShouldRenderValue={_.isEqual(isMulti, 'isMulti') ? true : !selected}
        isSearchable={false}
        placeholder={placeholder || t('forms.common.select')}
        isMulti={isMulti}
        closeMenuOnSelect={closeMenuOnSelect}
        menuIsOpen={selected}
      />
    );
  },
);

export const SelectField = ({ options, field, form, hasIcon, afterChange, ...rest }) => (
  <ReactSelect
    {...rest}
    options={options}
    components={hasIcon ? { Option: CountryIconOption } : {}}
    name={field.name}
    value={!Array.isArray(field.value) ? (options ? options.find((option) => option.value === field.value) : '') : field.value}
    onChange={(option) => {
      if (afterChange) {
        afterChange(option);
      }

      if (!_.isNull(option)) {
        if (!Array.isArray(option)) {
          form.setFieldValue(field.name, option.value);
        } else {
          form.setFieldValue(field.name, option);
        }
      } else {
        form.setFieldValue(field.name, []);
      }
    }}
    onBlur={field.onBlur}
  />
);

export default withNamespaces()(SelectField);
